import { useForm, SubmitHandler, FieldValues, FieldErrors, RegisterOptions, UseFormRegisterReturn } from "react-hook-form"
import { isLettersAndSpacesOnly, toLettersAndSpacesOnly } from '../../../utils/lettersValidation';

export interface NewCollaboratorForm {
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  id_branch: string;
  document_number: string;
  password: string;
  confirm_password: string;
  role_description: string;
}

const validatePhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber)
    return true;

  const allSameDigits = /^(\d)\1*$/.test(phoneNumber);
  const consecutiveDigits = /^(0123456789|1234567890|2345678901|3456789012|4567890123|5678901234|6789012345|7890123456|8901234567|9012345678|0987654321|9876543210|8765432109|7654321098|6543210987|5432109876|4321098765|3210987654|2109876543|1098765432)$/.test(phoneNumber);
  const length = phoneNumber.length == 10;

  if (allSameDigits) {
    return 'El número de teléfono no puede contener todos los dígitos iguales.';
  }
  if (consecutiveDigits) {
    return 'El número de teléfono no puede contener dígitos consecutivos.';
  }
  if (!length) {
    return 'El número de teléfono debe tener 10 dígitos.';
  }
  return true;
};

const REQUIRED_MESSAGE = 'Este campo es requerido.';
const NAME_MAX_LENGTH = 50;
const DOCUMENT_MAX_LENGTH = 20;
const DOCUMENT_PATTERN = /^[A-Za-z0-9]*$/;

const nameRules: RegisterOptions<NewCollaboratorForm, 'firstname' | 'lastname'> = {
  required: REQUIRED_MESSAGE,
  maxLength: { value: NAME_MAX_LENGTH, message: `Máximo ${NAME_MAX_LENGTH} caracteres.` },
  validate: {
    lettersOnly: (value) => isLettersAndSpacesOnly(value) || 'Solo se permiten letras y espacios.',
    noEdgeSpaces: (value) => value === value.trim() || 'No debe tener espacios al inicio ni al final.',
  },
};

const withLettersAndSpacesOnly = (registration: UseFormRegisterReturn): UseFormRegisterReturn => ({
  ...registration,
  onChange: (event) => {
    event.target.value = toLettersAndSpacesOnly(event.target.value);
    return registration.onChange(event);
  },
});

export const useNewCollaboratorForm = () => {

  const { register, handleSubmit, formState: { errors }, getValues, control} = useForm<NewCollaboratorForm>()

  const matchValidator = () => {
    const newPassword = getValues('password');
    const confirmPassword = getValues('confirm_password');

    if (newPassword !== confirmPassword) {
      return 'Las contraseñas no coinciden';
    }
    return;
  };

  return {
    controls: {
      firstname: withLettersAndSpacesOnly(register('firstname', nameRules)),
      lastname: withLettersAndSpacesOnly(register('lastname', nameRules)),
      email: register('email', {
        required: REQUIRED_MESSAGE,
        validate: (value) => value.includes('@') || 'El correo debe contener @',
      }),
      phone_number: register('phone_number', { validate: validatePhoneNumber }),
      id_branch: register('id_branch', { required: REQUIRED_MESSAGE }),
      document_number: register('document_number', {
        maxLength: { value: DOCUMENT_MAX_LENGTH, message: `Máximo ${DOCUMENT_MAX_LENGTH} caracteres.` },
        pattern: { value: DOCUMENT_PATTERN, message: 'Solo se permiten letras y números.' },
      }),
      role_description: register('role_description', {}),
      password: register('password', {
        pattern: {
          value: /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)([A-Za-z\d][\!\+\*\-\_\#\.\,]*){8,}$/,
          message: 'La contraseña no cumple los requerimientos mínimos.',
        },
        required: REQUIRED_MESSAGE,
      }),
      confirm_password: register('confirm_password', {
        validate: matchValidator,
        required: REQUIRED_MESSAGE,
      }),
    },
    handleSubmit,
    errors,
    formState: { errors },
    control,
  }
}
